import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download, Calendar as CalendarIcon, User } from "lucide-react";
import { toast, Toaster } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    pending: { c: "bg-[var(--rs-surface-2)] text-[var(--rs-text)]", l: "Offen" },
    ready: { c: "bg-[var(--rs-accent)]/20 text-[var(--rs-accent)]", l: "Bereit" },
    handed_over: { c: "bg-[var(--rs-primary)] text-white", l: "Übergeben" },
    returned: { c: "bg-black text-white", l: "Zurück" },
    completed: { c: "bg-[var(--rs-primary)]/15 text-[var(--rs-primary)]", l: "Abgeschlossen" },
    cancelled: { c: "bg-[var(--rs-error)]/15 text-[var(--rs-error)]", l: "Storniert" },
  };
  const m = map[status] || map.pending;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 ${m.c}`}>{m.l}</span>;
};

const MyContracts = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/contracts").then(({ data }) => {
      setItems(data);
    }).catch(() => toast.error("Fehler beim Laden")).finally(() => setLoading(false));
  }, []);

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/my/contracts/${c.id}/pdf`, { responseType: "blob" });
      if (res.data && res.data.type && res.data.type.includes("json")) {
        const text = await res.data.text();
        throw new Error(text || "Server-Fehler");
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.contract_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "PDF-Download fehlgeschlagen";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          if (json?.detail) msg = String(json.detail);
        } else if (e?.response?.data?.detail) {
          msg = String(e.response.data.detail);
        }
      } catch {}
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("PDF error:", e);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="rs-overline">NIVARA · KUNDE</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-2">
          <div>
            <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight leading-[1]">
              {t("my_title")}
            </h1>
            <p className="text-[var(--rs-text-muted)] mt-2">Hallo {user?.name}. {t("my_sub")}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/my/profile" className="rs-btn-secondary self-start" data-testid="my-profile-btn">
              <User size={16} className="mr-2" />
              {t("profile_title")}
            </Link>
            <Link to="/calendar" className="rs-btn-secondary self-start" data-testid="my-calendar-btn">
              <CalendarIcon size={16} className="mr-2" />
              {t("nav_calendar")}
            </Link>
            <Link to="/" className="rs-btn-primary self-start" data-testid="my-book-btn">
              <Plus size={16} className="mr-2" />
              {t("book_now")}
            </Link>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="text-center text-[var(--rs-text-muted)] py-20">…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 border-2 border-dashed border-[var(--rs-border)]" data-testid="my-empty">
              <div className="rs-overline">Noch keine Buchungen</div>
              <Link to="/" className="rs-btn-primary mt-6 inline-flex">
                <Plus size={16} className="mr-2" />
                {t("book_now")}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((c) => (
                <article key={c.id} className="bg-white border-2 border-[var(--rs-text)]" data-testid={`my-card-${c.id}`}>
                  <header className="flex items-start justify-between gap-4 p-5 border-b border-[var(--rs-border)] bg-[var(--rs-surface)]">
                    <div>
                      <div className="text-xs font-bold tracking-tight">{c.contract_number}</div>
                      <h3 className="font-[Chivo] text-lg font-black mt-1">{c.vehicle_name}</h3>
                      <div className="text-xs text-[var(--rs-text-muted)]">{c.vehicle_plate}</div>
                    </div>
                    <StatusBadge status={c.status} />
                  </header>
                  <div className="p-5 grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2">
                      <div className="rs-overline">{t("th_period")}</div>
                      <div className="text-xs mt-1">
                        {(c.rental_start || "").replace("T", " ")} → {(c.rental_end || "").replace("T", " ")}
                      </div>
                    </div>
                    <div>
                      <div className="rs-overline">Tarif</div>
                      <div className="font-bold mt-1">{c.rental_type === "hourly" ? "Stunde" : "24h"}</div>
                    </div>
                    <div>
                      <div className="rs-overline">{t("total_price")}</div>
                      <div className="font-[Chivo] text-lg font-black text-[var(--rs-primary)]">CHF {c.price_chf}.–</div>
                    </div>
                  </div>
                  <div className="px-5 pb-5 flex flex-wrap gap-2">
                    <button type="button" className="rs-btn-secondary !py-3 !px-4 !text-xs" onClick={() => downloadPdf(c)} data-testid={`my-pdf-${c.id}`}>
                      <Download size={14} className="mr-2" /> PDF
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyContracts;
